import { Input } from '@ohear/ui'

/**
 * ESIGN consent block.
 *
 * Renders the W-9 perjury statement (or other form-specific equivalent),
 * a typed-signature input, and exposes whether the typed name matches the
 * payee legal name on file. The parent uses `matchesLegalName` to enable
 * the actual save button — we don't render a button here so the signature
 * can sit at the bottom of a larger form and trigger the same submit.
 *
 * Compliance notes (15 USC §7001):
 *   - Consent requires actor's *active* selection (typing the name = action).
 *   - Server records IP + user agent + timestamp; the typed text is the
 *     "signature" of record.
 *   - The actor must have already provided their legal name (we surface
 *     "fill in your legal name above" guidance when payeeLegalName is null).
 */
export interface EsignBlockProps {
  payeeLegalName: string | null
  signatureValue: string
  onSignatureChange: (next: string) => void
  /** Translated, copy-vetted strings. Caller picks the right perjury statement
   *  based on form type (W-9 vs W-8). */
  copy: {
    perjuryStatementHeading: string
    perjuryStatementBody: string
    typeLegalNamePrompt: string
    typeLegalNameHint: string
    namePlaceholder: string
    legalNameMissing: string
  }
  disabled?: boolean
}

export function EsignBlock({
  payeeLegalName,
  signatureValue,
  onSignatureChange,
  copy,
  disabled = false,
}: EsignBlockProps) {
  const trimmedName = (payeeLegalName ?? '').trim()
  const matches = trimmedName.length > 0 && signatureValue.trim() === trimmedName

  return (
    <section className="flex flex-col gap-3 border-t border-[var(--border)] pt-4">
      <div>
        <h3 className="m-0 text-[14px] font-semibold">{copy.perjuryStatementHeading}</h3>
        <p className="m-0 mt-1 text-[12px] text-[var(--text-2)] whitespace-pre-line">
          {copy.perjuryStatementBody}
        </p>
      </div>

      {trimmedName ? (
        <label className="flex flex-col gap-1 text-[13px]">
          <span className="font-medium">{copy.typeLegalNamePrompt}</span>
          <Input
            value={signatureValue}
            onChange={(e) => onSignatureChange(e.target.value)}
            placeholder={copy.namePlaceholder}
            disabled={disabled}
            autoComplete="off"
          />
          <span className="text-[12px] text-[var(--text-2)]">{copy.typeLegalNameHint}</span>
        </label>
      ) : (
        <p className="text-[12px] text-[var(--text-2)] m-0">{copy.legalNameMissing}</p>
      )}

      {/* No render of `matches` — the parent reads it via `matchesLegalName()`
          on the same value. We export it here for callers that want to gate
          a Save button outside this block. */}
      <input type="hidden" data-testid="esign-matches" value={matches ? '1' : '0'} />
    </section>
  )
}

/** Convenience for parents: returns true when the signature matches the
 *  payee legal name exactly. Mirrored from EsignBlock's internal logic so
 *  callers don't have to peek at the hidden input. */
export function signatureMatchesLegalName(
  signature: string,
  payeeLegalName: string | null,
): boolean {
  const name = (payeeLegalName ?? '').trim()
  return name.length > 0 && signature.trim() === name
}
